import { gql } from "@apollo/client";
import {
  RiceFragment,
  outputResultDataFragment,
  ListFormValuesFragment,
  TypeImageColorFragment,
  packageFragment,
  UserRoleFragment,
  UserAttributeFragment,
} from "../fragment";

export const GET_USER_PROFILE = gql`
  query getUser {
    getUser {
      username
      millID
      millName
      country
      chalkyLevel
      damagedLevel
      yellowLevel
      undermilledLevel
      headLevel
      isAutoYellow
      defaultLanguage
      useBeta
      isMaExpired
      isBeforeMaExpired
      isFeedbackShowUp
      isShowInfoVersion
      flags {
        new_feature_full {
          flag
          isFirst
          isNotShow
        }
        new_feature_inspect {
          flag
          isFirst
          isNotShow
        }
        new_feature_result {
          flag
          isFirst
          isNotShow
        }
        new_feature_history {
          flag
          isFirst
          isNotShow
        }
        new_feature_profile {
          flag
          isFirst
          isNotShow
        }
        new_feature_setting {
          flag
          isFirst
          isNotShow
        }
        new_feature_preset {
          flag
          isFirst
          isNotShow
        }
        new_feature_whiteness {
          flag
          isFirst
          isNotShow
        }
        new_feature_whiteness_inspect {
          flag
          isFirst
          isNotShow
        }
        new_feature_whiteness_result {
          flag
          isFirst
          isNotShow
        }
        new_feature_whiteness_history {
          flag
          isFirst
          isNotShow
        }
      }
      package {
        ...packageFragment
      }
      attributes {
        ...UserAttribute
      }
      role {
        ...UserRoleFragment
      }
      inferenceSetting {
        settingLevel {
          value
          isAuto
          isAdvance
          inferenceSettingID
        }
        riceGroupName
        riceGroupID
      }
    }
  }
  ${packageFragment}
  ${UserAttributeFragment}
  ${UserRoleFragment}
`;

export const GET_USER_PROFILE_PACKAGE = gql`
  query getUserPackage {
    getUser {
      package {
        ...packageFragment
      }
    }
  }
  ${packageFragment}
`;

export const GET_USER_INFERENCE_SETTING = gql`
  query getUserDefaultInferenceSetting {
    getUser {
      inferenceSetting {
        settingLevel {
          value
          isAuto
          isAdvance
          inferenceSettingID
        }
        riceGroupName
        riceGroupID
      }
    }
  }
`;

export const GET_USER_PROFILE_ROLE = gql`
  query getUserRole {
    getUser {
      millID
      role {
        ...UserRoleFragment
      }
    }
  }
  ${UserRoleFragment}
`;

export const UPDATE_USER_PROFILE = gql`
  mutation updateUserData($userData: inputUser) {
    updateUserData(userData: $userData) {
      millID
      chalkyLevel
      damagedLevel
      yellowLevel
      undermilledLevel
      headLevel
      useBeta
      isAutoYellow
    }
  }
`;

export const UPDATE_USER_PROFILE_ATTRIBUTES = gql`
  mutation updateUserProfileAttributes($attributes: InputUserAttributes) {
    updateUserData(userData: { attributes: $attributes }) {
      attributes {
        name
        phoneNumber
        signature
      }
    }
  }
`;

export const UPDATE_USER_FLAG = gql`
  mutation updateUserFlag($updateFlag: inputFlag = {}) {
    updateUserFlag(updateFlag: $updateFlag) {
      isShowInfoVersion
      isFeedbackShowUp
    }
  }
`;

export const UPDATE_USER_FLAG_NEW_FEATURE = gql`
  mutation updateUserFlagNewFeature($key: String!, $isNotShow: Boolean) {
    updateUserFlagNewFeature(updateNewFeatureFlag: { key: $key, isNotShow: $isNotShow }) {
      flag {
        flag
        isFirst
        isNotShow
      }
    }
  }
`;

export const GET_INSPECT_OPTION = gql`
  query getInspectOption {
    getInspectOption {
      riceID
      riceTypeName
      isBeforeExpired
      expiredDate
      standard {
        imageStandard
        isDefault
        standardID
        standardName
        presetStandardID
        presetStandard {
          presetName
        }
      }
    }
  }
`;

export const GET_FORM = gql`
  query getForm($formID: String!) {
    getForm(formID: $formID) {
      formID
      formValue {
        value {
          id
          key
          grainRequire
          maxLength
          minLength
          name
          typeUnit
        }
        name
        column {
          detail
          standard
        }
        split
      }
      formName
    }
  }
`;

export const GET_FORM_BY_MILL = gql`
  query getFormByMill {
    getFormByMill {
      formName
      formID
      isDefault
    }
  }
`;

export const GET_INSPECT_STANDARD_OPTION = gql`
  query getInspectStandard($riceID: String, $standardID: String) {
    getInspectStandard(riceID: $riceID, standardID: $standardID) {
      formName
      formID
      isDefault
    }
  }
`;

export const GET_STANDARD_BY_ID = gql`
  query getStandardByID($standardID: String!) {
    getStandardByID(standardID: $standardID) {
      standard {
        presetStandard {
          presetName
          isAdvance
          settingLevel {
            inferenceSettingID
            isAdvance
            isAuto
            value
          }
        }
        isDefault
        imageStandard
        presetStandardID
        standardName
      }
      form {
        formName
        formID
        isDefault
      }
    }
  }
`;

export const GET_HISTORY = gql`
  query getHistory(
    $scanner: String
    $riceID: String
    $standardID: String
    $toDate: String
    $fromDate: String
    $processID: [String]
    $lastKey: String
    $limit: Int
    $featureType: String
  ) {
    getHistory(
      scanner: $scanner
      riceID: $riceID
      standardID: $standardID
      toDate: $toDate
      fromDate: $fromDate
      processID: $processID
      lastKey: $lastKey
      limit: $limit
      featureType: $featureType
    ) {
      content {
        myDate
        standardName
        formName
        riceTypeName
        username
        weight
        historyID
        note
        isBeta
        whiteness
        process {
          name
          key
          color
          dataForm {
            key
            name
            optionInput {
              type
              option
              min
              max
              length
              decimal
            }
            valueUnit
            values
            value
          }
        }
      }
      totalItem
      lastKey
    }
  }
`;

export const GET_HISTORY_BY_ID = gql`
  query getHistoryById($historyID: String!) {
    getHistoryById(historyID: $historyID) {
      myDate
      formName
      riceTypeName
      standardID
      standardName
      inferenceID
      imageStandard
      historyID
      weight
      note
      isBeta
      createBy
      createByProfle {
        email
        name
        phoneNumber
        signature
      }
      resultData {
        ...outputResultDataFragment
        whiteness
      }
      process {
        color
        dataForm {
          key
          name
          optionInput {
            type
            option
            min
            max
            length
            decimal
          }
          valueUnit
          values
          value
        }
        key
        name
      }
    }
  }
  ${outputResultDataFragment}
`;

export const GET_HISTORY_BY_SHARE_ID = gql`
  query getShareHistory($shareID: String!) {
    getShareHistory(shareID: $shareID) {
      myDate
      formName
      riceTypeName
      standardID
      standardName
      inferenceID
      imageStandard
      historyID
      weight
      note
      isBeta
      createBy
      createByProfle {
        email
        name
        phoneNumber
        signature
      }
      resultData {
        ...outputResultDataFragment
        whiteness
      }
      process {
        color
        dataForm {
          key
          name
          optionInput {
            type
            option
            min
            max
            length
            decimal
          }
          valueUnit
          values
          value
        }
        key
        name
      }
      whiteness
    }
  }
  ${outputResultDataFragment}
`;

export const GET_INFERENCE_BY_ID = gql`
  query getInference($inferenceID: [String]) {
    getInference(inferenceID: $inferenceID) {
      processedDate
      imageURL
      inferenceID
    }
  }
`;

export const CREATE_LIST_INSPECT_RESULT = gql`
  mutation createInspectResult(
    $dataRice: InputDataRiceWithoutFormValue!
    $inferenceID: [String]
    $resultData: inputResultData!
    $weight: Float
    $note: String
    $processFormID: String
    $processFormValue: AWSJSON
  ) {
    createInspectResult(
      dataRice: $dataRice
      inferenceID: $inferenceID
      resultData: $resultData
      weight: $weight
      note: $note
      processFormID: $processFormID
      processFormValue: $processFormValue
    ) {
      myDate
      inferenceID
      historyID
      weight
      resultData {
        ...outputResultDataFragment
      }
    }
  }
  ${outputResultDataFragment}
`;

export const DELETE_INSPECT_RESULT = gql`
  mutation deletedInspectResults($historyID: [String]!) {
    deletedInspectResults(historyID: $historyID) {
      inspectStatus
      historyID
    }
  }
`;

export const UPDATE_INSPECT_RESULT = gql`
  mutation updateInspectResult(
    $resultData: inputResultData!
    $historyID: String!
    $note: String
    $processFormValue: AWSJSON
  ) {
    updateInspectResult(
      resultData: $resultData
      historyID: $historyID
      note: $note
      processFormValue: $processFormValue
    ) {
      myDate
      formName
      riceTypeName
      standardName
      inferenceID
      imageStandard
      historyID
      weight
      note
      resultData {
        ...outputResultDataFragment
      }
    }
  }
  ${outputResultDataFragment}
`;

export const GET_EVODATA = gql`
  query getEvoData(
    $formID: String!
    $inferenceID: [String]
    $standardID: String!
    $isAutoYellow: Boolean
    $yellowLevel: String
    $averageLength: Float
  ) {
    getEvoData(
      standardID: $standardID
      inferenceID: $inferenceID
      formID: $formID
      yellowLevel: $yellowLevel
      isAutoYellow: $isAutoYellow
      averageLength: $averageLength
    ) {
      ...outputResultDataFragment
      whiteness
    }
  }
  ${outputResultDataFragment}
`;

export const SHARE_STATUS_RESULT = gql`
  mutation shareHistory($historyID: String!) {
    shareHistory(historyID: $historyID) {
      shareID
    }
  }
`;

export const GET_VIEW_STANDARD = gql`
  query getViewStandard($standardID: String, $formID: String, $shareID: String) {
    getViewStandard(formID: $formID, standardID: $standardID, shareID: $shareID) {
      standardName
      formName
      formValue {
        name
        value {
          condition
          condition_max
          condition_min
          grainRequire
          grainRequire_max
          grainRequire_min
          id
          key
          maxLength
          minLength
          name
          unit
        }
      }
      isDefault
      updateDate
    }
  }
`;

export const CREATE_SHARE_STANDARD = gql`
  mutation shereStandard($formID: String!, $standardID: String!) {
    shereStandard(formID: $formID, standardID: $standardID) {
      shareID
    }
  }
`;

export const GET_IMAGE_COLOR_INFERENCE = gql`
  query imageColorInference($setOfImageKey: [imageInputvalue] = {}) {
    imageColorInference(setOfImageKey: $setOfImageKey) {
      inferenceID
      imageUrls {
        source
        type
        shape
      }
      imageColorSet {
        shape
        type
      }
    }
  }
`;

// Ex . const editStandardData = [{
//   condition: "",
//   condition_max: "",
//   condition_min: "",
//   grainRequire: 1.5,
//   grainRequire_max: 1.5,
//   grainRequire_min: 1.5,
//   id: "",
//   key: "",
//   maxLength: 1.5,
//   minLength: 1.5,
//   name: "",
//   typeUnit: ""
// }]
export const EDIT_STANDARD = gql`
  mutation editStandard(
    $standardData: [editStandardData]!
    $standardID: String!
    $standardName: String!
  ) {
    editStandard(
      standardData: $standardData
      standardID: $standardID
      standardName: $standardName
    ) {
      statusCode
    }
  }
`;

export const GET_LIST_ROLE = gql`
  query getListRole {
    getListRole {
      id
      isAdmin
      roleName
      user
    }
  }
`;

export const GET_DROPDOWN_LIST_ROLE = gql`
  query getDropdownListRole {
    getListRole {
      id
      isAdmin
      roleName
    }
  }
`;

export const GET_ROLE_BY_ID = gql`
  query getRoleByID($roleID: String!) {
    getRoleByID(roleID: $roleID) {
      id
      isAdmin
      updateDate
      updateBy
      roleName
      organization {
        permission {
          action
          feature
        }
      }
      permission {
        feature
        action
      }
    }
  }
`;

export const UPDATE_PERMISSION_BY_ROLE = gql`
  mutation updatePermissionByRole($roleData: InputUserRole = {}) {
    updateRoleDetail(roleData: $roleData) {
      permission {
        action
        feature
      }
      updateBy
      updateDate
    }
  }
`;

export const GET_LIST_USER_ORGANIZATION = gql`
  query getListUserOrganization {
    getListUserOrganization {
      username
      attributes {
        email
        name
      }
      role {
        id
        roleName
      }
      subID
    }
  }
`;

export const GET_USER_ORGANIZATION_BY_ID = gql`
  query getUserOraganizationByID($subID: String = "") {
    getUserOraganizationByID(subID: $subID) {
      username
      attributes {
        email
        name
        phoneNumber
        signature
      }
      role {
        id
        isAdmin
      }
    }
  }
`;

export const UPDATE_USER_ROLE = gql`
  mutation updateUserRole($subID: String!, $roleID: String = "") {
    updateUserRole(roleID: $roleID, subID: $subID) {
      subID
      newRole
    }
  }
`;

export const GET_PROCESS_FORM = gql`
  query getProcessForm {
    getProcessForm {
      color
      key
      name
      dataForm {
        key
        name
        optionInput {
          type
          option
          min
          max
          length
          decimal
        }
        valueUnit
        values
        value
      }
    }
  }
`;

export const GET_LIST_DEVICE = gql`
  query getListDevice {
    getListDevice {
      deviceAttributes {
        name
        value
      }
      deviceCreateDate
      deviceKey
      deviceLastAuthenticatedDate
      deviceLastModifiedDate
    }
  }
`;

export const GET_AUTH_TOKEN_IAM = gql`
  query getAuthToken($path: String = "", $body: AWSJSON) {
    getAuthToken(path: $path, body: $body) {
      Authorization
      Host
      X_Amz_Date
      Content_Length
      Content_Type
      Accept
    }
  }
`;

export const UPDATE_INFERENCE_DEFAULT_SETTING = gql`
  mutation updateInferenceDefaultSetting(
    $InputInferenceDefaultSetting: InputInferenceDefaultSetting
  ) {
    updateInferenceDefaultSetting(InputInferenceDefaultSetting: $InputInferenceDefaultSetting) {
      riceGroupID
    }
  }
`;

export const GET_PRESET_STANDARD = gql`
  query getPresetStandard {
    getPresetStandard {
      presetName
      presetStandardID
      riceGroup {
        riceName
        riceID
        standard {
          standardID
          standardName
        }
      }
    }
  }
`;

export const GET_PRESET_STANDARD_BY_ID = gql`
  query getPresetStandardByID($id: String = "") {
    getPresetStandardByID(id: $id) {
      isAdvance
      presetName
      presetStandardID
      standardIDs
      settingLevel {
        inferenceSettingID
        isAdvance
        isAuto
        value
      }
      riceGroupName
      riceGroupID
      riceGroup {
        riceID
        riceName
        standard {
          standardID
          standardName
          presetStandardID
          presetStandard {
            presetName
          }
        }
      }
    }
  }
`;

export const GET_RICE_GROUP = gql`
  query getRiceGroup {
    getRiceGroup {
      riceGroupID
      riceGroupName
      settingLevel {
        inferenceSettingID
        isAdvance
        isAuto
        value
      }
      riceGroup {
        riceName
        riceID
        standard {
          standardID
          standardName
          presetStandardID
          presetStandard {
            presetName
          }
        }
      }
    }
  }
`;

export const CREATE_PRESET_STANDARD = gql`
  mutation createPresetStandard($createPreset: InputCreatePresetStandard = {}) {
    createPresetStandard(createPreset: $createPreset) {
      statusCode
      presetName
      presetStandardID
      isAdvance
    }
  }
`;

export const UPDATE_PRESET_STANDARD = gql`
  mutation updatePresetStandard($updatePreset: InputUpdatePresetStandard = {}) {
    updatePresetStandard(updatePreset: $updatePreset) {
      statusCode
      presetName
      presetStandardID
      isAdvance
    }
  }
`;

export const DELETE_PRESET_STANDARD_BY_ID = gql`
  mutation deletePresetStandardByID($presetStandardID: [String] = "") {
    deletePresetStandardByID(presetStandardID: $presetStandardID) {
      presetStatus
    }
  }
`;
